import axios, { AxiosRequestConfig } from 'axios'
import { apiUrl, CSRF_COOKIE_NAME } from './config'

const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null
  return null
}

const getGuestToken = (): string | undefined => {
  return localStorage.getItem('guest-token')?.valueOf()
}

// Axios -----------------

const api = axios.create({
  baseURL: apiUrl,
  withCredentials: true,
  xsrfCookieName: CSRF_COOKIE_NAME,
  xsrfHeaderName: 'X-CSRF-TOKEN'
})

// Add the Anti-Replay Timestamp
api.interceptors.request.use((config) => {
  config.headers = config.headers || {}

  // Guest token for /viewer/:imageId route
  const guestToken = getGuestToken()
  if (guestToken) config.headers['Guest-Apikey'] = guestToken

  // Read the latest CSRF cookie and ensure it's pinned to the headers
  const csrfToken = getCookie(CSRF_COOKIE_NAME)
  if (csrfToken) config.headers['X-CSRF-TOKEN'] = csrfToken

  return config
})

// Catch 401 Expirations
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      try {
        await axios.get(`${apiUrl}/auth/refresh`, { withCredentials: true })

        const freshCsrfToken = getCookie(CSRF_COOKIE_NAME)
        const freshGuestToken = getGuestToken()

        if (freshCsrfToken && originalRequest.headers) originalRequest.headers['X-CSRF-TOKEN'] = freshCsrfToken
        if (freshGuestToken && originalRequest.headers) originalRequest.headers['Guest-Apikey'] = freshGuestToken

        originalRequest.headers = originalRequest.headers || {}

        return api(originalRequest)
      } catch (refreshError) {
        console.error("Refresh token dead. Guest session expired completely.")
        return Promise.reject(refreshError)
      }
    }
    return Promise.reject(error)
  }
)

export async function get(url: string) {
  const res = await api.get(url)
  return res.data
}

export async function BlockOrGetData(url: string, needToken: boolean = false) {
  if (needToken) {
    const res = await api.get(url)
    return res
  }
  const res = await axios.get(url)
  return res
}

export async function post(url: string, data: any) {
  const res = await api.post(url, data)
  return res.data
}

export async function postFile(url: string, data: FormData) {
  const res = await api.post(url, data, {headers: {'Content-Type': 'multipart/form-data' }})
  return res.data
}

export async function customPost(url: string, data: any, config?: AxiosRequestConfig) {
  const res = await axios.post(url, data, config)
  return res.data
}

export async function put(url: string, data: any) {
  const res = await api.put(url, data)
  return res.data
}

export async function remove(url: string, data: any = {}) {
  const res = await api.delete(url, { data })
  return res.data
}

// Fetch -----------------

async function managedFetch(url: string, options: RequestInit = {}): Promise<Response> {
  options.credentials = 'include'

  const csrfToken = getCookie(CSRF_COOKIE_NAME)
  const guestToken = getGuestToken()

  options.headers = {
    ...options.headers,
    'Content-Type': 'application/json',
    ...(csrfToken && { 'X-CSRF-TOKEN': csrfToken }),
    ...(guestToken && { 'Guest-Apikey': guestToken })
  }

  let res = await fetch(url, options)

  // If access cookie has expired, run the same recovery routine as Axios
  if (res.status === 401 && !(options as any)._retry) {
    (options as any)._retry = true
    try {
      const refreshRes = await fetch(`${apiUrl}/auth/refresh`, { credentials: 'include' })
      if (refreshRes.ok) {
        const freshCsrfToken = getCookie(CSRF_COOKIE_NAME)
        const freshGuestToken = getGuestToken()

        options.headers = {
          ...options.headers,
          ...(freshCsrfToken && { 'X-CSRF-TOKEN': freshCsrfToken }),
          ...(freshGuestToken && { 'Guest-Apikey': freshGuestToken })
        }

        return await fetch(url, options)
      }
    } catch (err) {
      console.error("Fetch silent refresh failed", err)
    }
  }
  return res
}

export async function fetchGet(endpoint: string, signal?: AbortSignal) {
  return await managedFetch(`${apiUrl}/${endpoint}`, { method: 'GET', signal })
}

export async function fetchPost(endpoint: string, payload: any, signal?: AbortSignal) {
  return await managedFetch(`${apiUrl}/${endpoint}`, {
    method: 'POST',
    body: JSON.stringify(payload),
    signal
  })
}
